import React from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useI18n } from 'contexts/i18n'
import { useDocuments } from 'contexts/documents'
import { useCheckDocumentPermission } from 'pages/documents/utilities'
import { compact } from 'utilities/array'
import { safeFormat } from 'utilities/date-time'
import { isAfter, endOfToday, differenceInDays, startOfDay } from 'date-fns'
import {
    Hero, Heading, Status, Deadline,
    Signatories, SubheadingColumns, Subheading
} from './s'
import { CloseButton } from 'components/button'
import Progress from 'components/progress'
import { CaptionSmall } from 'components/typography/caption'
import Link from 'components/link'
import StandaloneTimePicker from 'components/form/field/time/standalone'
import { FlexChildShrink } from 'components/flex'
import Remind from './remind'
import Signatory from './signatory'
import Meta from 'components/meta'

const SigningStatus = ({ dokument, dismiss, salt }) => {
    const { formatMessage } = useIntl()
    const { dateLocale: locale } = useI18n()

    const { postponeSigningDeadline } = useDocuments()
    const checkDocumentPermission = useCheckDocumentPermission()

    if(!dokument?.signing) {
        return null
    }

    const {
        createdBy,
        createdAt,
        status,
        signatories,
        dueAt,
        lastRemindedAt
    } = dokument.signing

    const postpone = async date => {
        const days = differenceInDays(startOfDay(new Date(date)), startOfDay(new Date(dueAt)))
        if(!days) {
            return
        }

        postponeSigningDeadline(days, dokument.id)
    }

    const { signable } = checkDocumentPermission(dokument, true)

    const unsigned = signatories.filter(({ signedAt }) => !signedAt)
    const signed = signatories.filter(({ signedAt }) => !!signedAt)
    const total = signatories.length
    const now = new Date()

    return (
        <>
            <CloseButton
                onClick={dismiss}
                autoFocus={false} />
            <Hero>
                <Heading>
                    <FormattedMessage
                        id="documents_signing_status_label"
                        defaultMessage="Signing status" />
                </Heading>
                <Status>
                    {(signed.length > 0) && (
                        <Progress
                            completed={signed.length}
                            total={total}
                            mode="compact" />
                    )}
                    <FormattedMessage
                        id="document_signing_count_of_total_signed"
                        defaultMessage="{count, plural, =0 {{count} of {total} have signed} =1 {{count} of {total} has signed} other {{count} of {total} have signed}}"
                        values={{
                            count: signed.length,
                            total
                        }} />
                </Status>
                <Deadline>
                    <CaptionSmall className="compact">
                        <FormattedMessage
                            id="document_signing_deadline"
                            defaultMessage="Signing deadline: {date}"
                            values={{ date: safeFormat(dueAt, 'PPP', { locale }) }} />
                    </CaptionSmall>
                    {(signable && ['timedout', 'pending'].includes(status)) && (
                        <StandaloneTimePicker
                            time={dueAt ? new Date(dueAt) : now}
                            past={false}
                            blockedDateRanges={compact([
                                { on: now },
                                (dueAt && isAfter(new Date(dueAt), endOfToday())) && {
                                    start: now,
                                    end: new Date(dueAt)
                                }
                            ])}
                            trigger={(
                                <CaptionSmall className="compact">
                                    <Link className="constructive">
                                        <FormattedMessage
                                            id="action_postpone"
                                            defaultMessage="Postpone" />
                                    </Link>
                                </CaptionSmall>
                            )}
                            label={formatMessage({
                                id: 'document_signing_action_postpone_deadline',
                                defaultMessage: 'Postpone signing deadline'
                            })}
                            onChange={postpone}
                            salt={`${salt}:postpone-signing-deadline`} />
                    )}
                </Deadline>
            </Hero>
            {!!unsigned.length && (
                <Signatories {...(!signed.length ? { className: 'compact' } : null)}>
                    <SubheadingColumns>
                        <FlexChildShrink>
                            <Subheading>
                                <FormattedMessage
                                    id="document_signing_status_pending"
                                    defaultMessage="Pending" />
                            </Subheading>
                        </FlexChildShrink>
                        {!!signable && (
                            <FlexChildShrink>
                                <Remind
                                    dokument={dokument}
                                    key={`${salt}:remind:${dokument?.id}:${lastRemindedAt}`} />
                            </FlexChildShrink>
                        )}
                    </SubheadingColumns>
                    {unsigned.map(signatory => (
                        <Signatory
                            {...signatory}
                            key={`${salt}:signatory:${signatory.user.email}`} />
                    ))}
                </Signatories>
            )}
            {!!signed.length && (
                <Signatories className="compact">
                    <Subheading>
                        <FormattedMessage
                            id="document_signing_status_signed"
                            defaultMessage="Signed" />
                    </Subheading>
                    {signed.map(signatory => (
                        <Signatory
                            {...signatory}
                            key={`${salt}:signatory:${signatory.user.email}`} />
                    ))}
                </Signatories>
            )}
            {(signed.length === total) && (
                <Meta heading={{
                    id: 'document_signing_completed_label',
                    defaultMessage: 'Signing completed'
                }} />
            )}
            <Meta
                type="sent"
                by={createdBy}
                at={createdAt}
                time="fixed" />
        </>
    )
}

export default SigningStatus
